import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, DropdownMenu, Icon, Label, Loader, TextArea } from '@gravity-ui/uikit';
import CirclePause from '@gravity-ui/icons/CirclePause';
import Ellipsis from '@gravity-ui/icons/Ellipsis';
import TrashBin from '@gravity-ui/icons/TrashBin';
import { useHistory } from 'react-router-dom';
import { lengthConfig } from '@paskills/validation';
import clx from 'classnames';
import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/DraftControlPanel';
import * as errorsKeyset from 'i18n/SettingsErrors';
import { DraftStatus, SmartHomeValidationStatuses } from 'client/model/draft';
import { useIsDraft } from 'client/hooks/useIsDraft';
import { Channel } from 'client/model/skill';
import { FormChangeHandler, FormErrors } from 'client/hocs/types';
import { InstallableAppFormSettings } from 'client/types';
import { SettingsPageContext } from 'client/context/settingsPageContext';
import {
    cancelSkillReview,
    deleteApp,
    requestInstallableAppReview,
    requestInstallableAppDeploy,
    requestCancelInstallableAppModeration,
    forceStopApp,
    stopApp,
} from 'client/api';
import { Context as AppStoreSnapshotContext } from 'client/context/appStoreSnapshotContext';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { routes } from 'client/lib/routes';
import { isPrivateSkillsEnabled } from 'client/utils';
import { ModalWithConfirmation } from 'client/blocks-redesign/ModalWithConfirmation/ModalWithConfirmation';
import { makeValidationErrorMessage } from 'client/blocks/DraftControlPanel/DraftControlPanel';

import styles from './ControlPanel.module.scss';

type ActionType = 'requestReview' | 'cancelReview' | 'changeToDraft' | 'deleteApp' | 'stopApp' | 'forceStopApp';

const t = i18n(keyset);
const tError = i18n(errorsKeyset);

interface ControlPanelProps {
    afterReviewRequest?: () => void;
    handleSaveDraft?: (e?: React.FormEvent) => Promise<void>;
    isNonValidationError: boolean;
    settingValues: InstallableAppFormSettings;
    onSettingValuesChange: FormChangeHandler<InstallableAppFormSettings>;
    settingFormErrors: FormErrors<InstallableAppFormSettings>;
}

type SkillActions = {
    error: string;
    hasPendingAction: boolean;
    statuses?: SmartHomeValidationStatuses;
};

const emptyPromiseFn = async () =>
    new Promise<void>((res, _rej) => {
        res();
    });

const useSkillActionsWrapper = () => {
    const { refreshAppStoreSnapshot = emptyPromiseFn } = useContext(AppStoreSnapshotContext);
    const { aliceApp, fetchOperations } = useContext(CurrentAliceAppContext)!;
    const [{ error, hasPendingAction, statuses }, setState] = useState<SkillActions>({
        error: '',
        hasPendingAction: false,
    });

    interface WrapHooks {
        onDone?: () => void;
        onFail?: () => void;
        onClean?: () => void;
    }

    const wrap = useCallback(
        function <T>(action: (InstallableAppId: string, channel: Channel) => Promise<T | void>, hooks?: WrapHooks) {
            return async () => {
                const { onDone, onFail, onClean } = hooks ?? {};
                setState({ hasPendingAction: true, error: '' });
                let result;
                try {
                    result = await action(aliceApp.id, aliceApp.channel);
                    setState({ hasPendingAction: false, error: '' });
                    !!onDone && onDone();
                } catch (e) {
                    const message = makeValidationErrorMessage(aliceApp.channel, e);
                    await refreshAppStoreSnapshot();
                    const inferError = e as { payload: { statuses: SmartHomeValidationStatuses } } | undefined;
                    setState({ hasPendingAction: false, error: message, statuses: inferError?.payload?.statuses });
                    !!onFail && onFail();
                }
                await fetchOperations();
                !!onClean && onClean();
                return result;
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [aliceApp, refreshAppStoreSnapshot],
    );

    return {
        wrapper: wrap,
        error,
        hasPendingAction,
        statuses,
    };
};

export const ControlPanel: React.FC<ControlPanelProps> = ({
    handleSaveDraft = () => {},
    isNonValidationError,
    settingValues,
    onSettingValuesChange,
    settingFormErrors,
}) => {
    const [forceStopComment, setForceStopComment] = useState<string>();
    const { aliceApp, operations } = useContext(CurrentAliceAppContext)!;
    const { refreshAppStoreSnapshot = emptyPromiseFn } = useContext(AppStoreSnapshotContext);
    const { hasUnsavedChanges } = useContext(SettingsPageContext);
    const { isDraft, setIsDraft } = useIsDraft(aliceApp.channel, aliceApp.id);

    const { wrapper, error, hasPendingAction } = useSkillActionsWrapper();
    const [openModalType, setOpenModalType] = useState<ActionType | undefined>();
    const history = useHistory();
    const isAdmin = __appConfig.user.isAdmin;

    useEffect(() => {
        setForceStopComment(undefined);
    }, [aliceApp.id]);

    const formErrors = Object.entries(settingFormErrors || {})
        .map(([_key, value]) => (Array.isArray(value) ? value.filter(Boolean).join(' ') : String(value)))
        .filter((errorMessage) => !!(errorMessage || '').trim());
    if (isNonValidationError) {
        formErrors.push(tError('unknown-error'));
    }

    const cancelReview = useCallback(async () => {
        const cancelReviewAndFetchOperations =
            aliceApp.channel === Channel.TvApp
                ? async (skillId: string) => {
                      await requestCancelInstallableAppModeration(skillId, aliceApp.channel);
                  }
                : cancelSkillReview;
        wrapper(cancelReviewAndFetchOperations, {
            onClean: () => {
                setOpenModalType(undefined);
                refreshAppStoreSnapshot();
            },
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aliceApp.id, wrapper]);

    const deleteInstallableApp = useCallback(async () => {
        const deleteWrapped = async (appId: string) => {
            await deleteApp(appId, aliceApp.channel);
        };
        const action = wrapper(deleteWrapped, {
            onDone: () => {
                setTimeout(async () => {
                    history.push(routes.app.installableApps(aliceApp.channel));
                    refreshAppStoreSnapshot();
                }, 3000);
            },
            onClean: () => setOpenModalType(undefined),
        });
        return await action();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aliceApp.id, wrapper]);

    const requestDeploy = useCallback(async () => {
        const saveAndRequestDeploy = async (skillId: string) => {
            await requestInstallableAppDeploy(skillId, aliceApp.channel);
        };

        const action = wrapper(saveAndRequestDeploy, {
            onDone: () => refreshAppStoreSnapshot(),
        });

        return await action();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasUnsavedChanges, aliceApp.id, wrapper]);

    const displaySkillAccess = isPrivateSkillsEnabled();

    const requestReview = useCallback(async () => {
        const saveAndRequestReview = async (skillId: string) => {
            await handleSaveDraft();
            await requestInstallableAppReview(skillId, aliceApp.channel);
        };

        const action = wrapper(saveAndRequestReview, {
            onDone: () => refreshAppStoreSnapshot(),
        });

        await action();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aliceApp, displaySkillAccess, wrapper]);

    const stopInstallableApp = useCallback(async () => {
        const stop = async (skillId: string) => {
            await stopApp(skillId, aliceApp.channel);
        };
        const action = wrapper(stop, {
            onDone: () => refreshAppStoreSnapshot(),
            onClean: () => setOpenModalType(undefined),
        });
        return await action();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aliceApp, displaySkillAccess, wrapper]);

    const forceStopInstallableApp = async () => {
        if (!forceStopComment) return;
        const forceStop = async (skillId: string) => {
            await forceStopApp(skillId, aliceApp.channel, { comment: forceStopComment });
        };
        const action = wrapper(forceStop, {
            onDone: () => refreshAppStoreSnapshot(),
            onClean: () => setOpenModalType(undefined),
        });
        return await action();
    };

    if (!operations.length) {
        return (
            <div className={styles.buttonsContainer}>
                <Loader />
            </div>
        );
    }

    const actions = {
        cancelReview: async () => {
            await cancelReview();
        },
        deleteInstallableApp: async () => {
            await deleteInstallableApp();
        },
        requestDeploy: async () => {
            await requestDeploy();
        },
        requestReview: async () => {
            await requestReview();
        },
        stopInstallableApp: async () => {
            await stopInstallableApp();
        },
        forceStopInstallableApp: async () => {
            await forceStopInstallableApp();
        },
    };

    let disableModerationButton = !aliceApp.draft.isAllowedForDeploy || (aliceApp.isBanned && !isAdmin);
    const disableStopButton = false;

    let hideSaveButton = true;
    let hideModerationButton = true;
    let hideStopButton = false;
    let hideDeployButton = true;
    let hideChangeButton = false;

    if (isDraft) {
        disableModerationButton = !aliceApp.draft.isAllowedForDeploy || (aliceApp.isBanned && !isAdmin);

        hideSaveButton = aliceApp.draft.status !== DraftStatus['InDevelopment'];
        hideModerationButton = !isDraft || aliceApp.draft.status !== DraftStatus['InDevelopment'];
        hideStopButton = true;
        hideDeployButton = aliceApp.draft.status !== DraftStatus['ReviewApproved'];
        hideChangeButton = aliceApp.draft.status === DraftStatus['InDevelopment'];
    }

    let modal = null;

    if (openModalType === 'requestReview') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('send-to-moderation')}
                title={t('ready-for-moderation')}
                onConfirm={() => actions.requestReview()}
                loading={hasPendingAction}
            >
                <p className={styles.grayNote}>{t('moderator-comment-placeholder')}</p>
                <TextArea
                    className={styles.moderatorCommentTextfield}
                    error={settingFormErrors.noteForModerator}
                    note={
                        <span className={styles.grayNote}>
                            {lengthConfig.noteForModerator - (settingValues.noteForModerator?.length || 0)}
                        </span>
                    }
                    onChange={(e) => onSettingValuesChange('noteForModerator')(e.target.value)}
                    placeholder={t('moderator-comment-placeholder')}
                    rows={5}
                    size="xl"
                    value={settingValues.noteForModerator}
                />
            </ModalWithConfirmation>
        );
    }
    if (openModalType === 'cancelReview') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('change')}
                title={t('changing-data-will-stop-moderation')}
                text={t('if-you-want-to-change-the-data')}
                onConfirm={() => actions.cancelReview()}
                loading={hasPendingAction}
            />
        );
    }
    if (openModalType === 'changeToDraft') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('go-to-draft')}
                title={t('change-in-copy')}
                text={t('you-can-not-change-published-version')}
                onConfirm={() => setIsDraft(true, aliceApp.channel)}
                loading={hasPendingAction}
            />
        );
    }
    if (openModalType === 'deleteApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('delete')}
                text={t('delete-text')}
                title={t('delete-title')}
                onConfirm={() => actions.deleteInstallableApp()}
                loading={hasPendingAction}
            />
        );
    }
    if (openModalType === 'stopApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('stop')}
                text={t('stop-text')}
                title={t('stop-title')}
                onConfirm={() => actions.stopInstallableApp()}
                loading={hasPendingAction}
            />
        );
    }
    if (openModalType === 'forceStopApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('stop')}
                title={t('stop-title')}
                text={t('stop-text')}
                onConfirm={() => actions.forceStopInstallableApp()}
                loading={hasPendingAction}
                confirmDisabled={!forceStopComment}
            >
                <TextArea
                    className={styles.moderatorCommentTextfield}
                    error={settingFormErrors.noteForModerator}
                    note={
                        <span className={styles.grayNote}>
                            {lengthConfig.noteForModerator - (settingValues.noteForModerator?.length || 0)}
                        </span>
                    }
                    onChange={(e) => setForceStopComment(e.target.value)}
                    placeholder={t('force-stop-not-empty-comment')}
                    rows={5}
                    size="xl"
                    value={forceStopComment}
                    errorMessage={!forceStopComment && t('force-stop-not-empty-comment')}
                />
            </ModalWithConfirmation>
        );
    }

    return (
        <div>
            <div className={styles.buttonsContainer}>
                <Button
                    className={clx(hideSaveButton && styles.button_hidden)}
                    loading={hasPendingAction}
                    type="submit"
                >
                    {t('save')}
                </Button>
                <Button
                    className={clx(hideModerationButton && styles.button_hidden)}
                    view={aliceApp.draft.isAllowedForDeploy ? 'action' : 'normal'}
                    disabled={disableModerationButton}
                    onClick={() => setOpenModalType('requestReview')}
                    loading={hasPendingAction}
                >
                    {t('send-to-moderation')}
                </Button>
                <Button
                    className={clx(hideStopButton && styles.button_hidden)}
                    disabled={disableStopButton}
                    onClick={() => setOpenModalType(isAdmin ? 'forceStopApp' : 'stopApp')}
                    loading={hasPendingAction}
                >
                    <Icon data={CirclePause} />
                    {t('stop')}
                </Button>
                <Button
                    className={clx(hideDeployButton && styles.button_hidden)}
                    view="action"
                    onClick={() => actions.requestDeploy()}
                    loading={hasPendingAction}
                >
                    {t('action-deploy')}
                </Button>
                <Button
                    className={clx(hideChangeButton && styles.button_hidden)}
                    onClick={() => setOpenModalType(isDraft ? 'cancelReview' : 'changeToDraft')}
                    loading={hasPendingAction}
                >
                    {t('change')}
                </Button>
                <DropdownMenu
                    renderSwitcher={(props) => (
                        <Button {...props}>
                            <Icon size={16} data={Ellipsis} />
                        </Button>
                    )}
                    items={[
                        {
                            action: () => setOpenModalType('deleteApp'),
                            iconStart: <Icon size={16} data={TrashBin} />,
                            text: t('delete'),
                            theme: 'danger',
                        },
                    ]}
                />
            </div>
            <div className={clx(styles.errorsBlock, !error && formErrors.length === 0 && styles.errorsBlock_hidden)}>
                {error && <Label theme="danger">{error}</Label>}
                {formErrors.map((errorMessage) => (
                    <Label key={errorMessage} theme="danger">
                        {errorMessage}
                    </Label>
                ))}
            </div>
            {modal}
        </div>
    );
};
